import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { Loader2, Save, Settings as SettingsIcon, Eye } from 'lucide-react';
import { getAgentSettings, upsertAgentSettings, DEFAULT_SETTINGS, listAgentSubBrands } from '../services/branding';
import type { AgentSettings } from '../types';
import type { SubBrand } from '@rehub/branding';
import { useToast } from './Toast';

interface SettingsScreenProps {
  user: User;
}

export default function SettingsScreen({ user }: SettingsScreenProps) {
  const [settings, setSettings] = useState<Partial<AgentSettings>>({ ...DEFAULT_SETTINGS, agent_id: user.id });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subBrands, setSubBrands] = useState<SubBrand[]>([]);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, brands] = await Promise.all([
        getAgentSettings(user.id),
        listAgentSubBrands().catch(() => [] as SubBrand[]),
      ]);
      if (!cancelled) {
        setSettings(s ?? { ...DEFAULT_SETTINGS, agent_id: user.id });
        setSubBrands(brands);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const selectedSubBrand = subBrands.find(b => b.id === settings.sub_brand_id) ?? null;

  const update = <K extends keyof AgentSettings>(key: K, value: AgentSettings[K]) =>
    setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertAgentSettings(user.id, settings);
      toast.success('Settings saved.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <SettingsIcon size={20} className="text-emerald-600" /> Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Branding shown on letters and the landing page that prospects see when they scan a QR code.
          </p>
        </div>

        {subBrands.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 space-y-3">
            <Field label="Team brand kit" hint="Pick a team sub-brand. Empty fields below will fall back to its logo / colour / agency name on landing pages and letters.">
              <select
                value={settings.sub_brand_id ?? ''}
                onChange={(e) => update('sub_brand_id', (e.target.value || null) as any)}
                className="input"
              >
                <option value="">Personal branding (no team kit)</option>
                {subBrands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}{b.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
            {selectedSubBrand && selectedSubBrand.locked_fields.length > 0 && (
              <p className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                Marketing locked: {selectedSubBrand.locked_fields.join(', ')}. Those fields are taken from the kit and cannot be overridden here.
              </p>
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          <Field label="Agency name">
            <input
              value={settings.agency_name ?? ''}
              onChange={(e) => update('agency_name', e.target.value)}
              className="input"
              placeholder="Smith & Co Estate Agents"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Your full name">
              <input
                value={settings.agent_full_name ?? ''}
                onChange={(e) => update('agent_full_name', e.target.value)}
                className="input"
                placeholder="Sarah Smith"
              />
            </Field>
            <Field label="Primary brand colour">
              <input
                type="color"
                value={settings.primary_color ?? '#0f172a'}
                onChange={(e) => update('primary_color', e.target.value)}
                className="w-full h-10 border border-slate-200 rounded-lg cursor-pointer"
              />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Contact email">
              <input
                type="email"
                value={settings.contact_email ?? ''}
                onChange={(e) => update('contact_email', e.target.value)}
                className="input"
                placeholder="sarah@smithco.co.uk"
              />
            </Field>
            <Field label="Contact phone">
              <input
                value={settings.contact_phone ?? ''}
                onChange={(e) => update('contact_phone', e.target.value)}
                className="input"
                placeholder="01234 567890"
              />
            </Field>
          </div>

          <Field label="Logo URL" hint="Public URL to a square or wide logo (PNG / SVG, max 200KB).">
            <input
              value={settings.logo_url ?? ''}
              onChange={(e) => update('logo_url', e.target.value)}
              className="input"
              placeholder="https://…/logo.png"
            />
            {settings.logo_url && (
              <img src={settings.logo_url} alt="logo preview" className="mt-2 max-h-12" />
            )}
          </Field>

          <Field
            label="Notification email"
            hint="Where new lead notifications are sent (defaults to your account email)."
          >
            <input
              type="email"
              value={settings.notification_email ?? ''}
              onChange={(e) => update('notification_email', e.target.value)}
              className="input"
              placeholder={user.email ?? ''}
            />
          </Field>
        </div>

        <div className="flex items-center justify-between mt-6">
          <a
            href="/l/preview?u=preview"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            <Eye size={14} /> Preview landing page
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save changes
          </button>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          font-size: 0.875rem;
          background: white;
          color: #0f172a;
        }
        .input:focus { outline: none; border-color: #34d399; }
      `}</style>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
